import { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Sparkles, X, Camera, Check } from "lucide-react";

/**
 * P2 Schritt 2 – optionaler Voranalyse-Dialog.
 *
 * Zeigt die von `POST /courses/pre-analyze` gelieferten Rückfragen an. Vollständig
 * OPTIONAL: der Nutzer kann alle, einige oder keine Fragen beantworten und
 * jederzeit direkt erstellen. Kein Draft, kein LocalStorage – ausschließlich
 * lokaler React-State (ein Reload verwirft den Dialog).
 *
 * Cleanup 30.08.2026: Der frühere Gefahrstoff-Zweig (Phase A · Schritt D,
 * 22.08.2026) wurde ausgebaut. Er war seit der Betreiberentscheidung vom
 * 24.08.2026 toter Code — bei `hazmat_signal=true` öffnet Unterweisungen.jsx
 * direkt das kombinierte `HazmatSdbSightingModal`; dieser Dialog wird dann
 * gar nicht mehr angezeigt. Die Substitutionsprüfung existiert seither nur
 * noch an genau einer Stelle (HazmatSdbSightingModal.jsx).
 */

export const PreAnalyzeDialog = ({
  data,
  submitting,
  onDirectCreate,
  onCreateWithAnswers,
  onCancel,
  onUploadNameplate,
  seed,
}) => {
  const open = !!data;
  const questions = (data?.questions || []).slice(0, 5);
  // P3-UX: Gerätefragen (maps_to product.*) → optionaler Typenschild-Weg (nur dann sichtbar).
  const hasDeviceQuestions = questions.some((q) => (q.maps_to || "").startsWith("product."));
  const [answers, setAnswers] = useState({});
  // P3-UX Schritt 5: Herkunft je Frage (generisch: "Typenschild"/"PDF"/"GTIN"/…), rein zur Anzeige.
  const [provenance, setProvenance] = useState({});

  // Lokalen Zustand verwerfen, sobald der Dialog schließt (kein Leak zwischen Sitzungen).
  useEffect(() => {
    if (!data) {
      setAnswers({});
      setProvenance({});
    }
  }, [data]);

  // P3-UX Schritt 5 (Variante A): bestätigte Werte (z. B. aus Typenschild) füllen die passenden
  // Gerätefragen NUR, wenn das Feld noch LEER ist. Bereits Eingetragenes bleibt unberührt.
  useEffect(() => {
    if (!seed || !seed.values) return;
    const fills = {};
    const marks = {};
    for (const qq of questions) {
      const mapsTo = qq.maps_to || "";
      if (!mapsTo.startsWith("product.")) continue;
      const key = mapsTo.slice("product.".length);
      const val = (seed.values[key] || "").trim();
      if (val && !(answers[qq.id] || "").trim()) {
        fills[qq.id] = val;
        marks[qq.id] = seed.source || "Übernommen";
      }
    }
    if (Object.keys(fills).length > 0) {
      setAnswers((a) => ({ ...a, ...fills }));
      setProvenance((p) => ({ ...p, ...marks }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [seed]);

  // Manuelle Änderung löscht die Herkunft (der Wert stammt dann nicht mehr rein aus der Quelle).
  const setAnswer = (id, value) => {
    setAnswers((a) => ({ ...a, [id]: value }));
    setProvenance((p) => (p[id] ? { ...p, [id]: null } : p));
  };

  const handleWithAnswers = () => {
    // Nur beantwortete (nicht-leere) Fragen sammeln – jede Frage ist freiwillig.
    const collected = questions
      .filter((q) => (answers[q.id] || "").trim().length > 0)
      .map((q) => ({ id: q.id, question: q.question, answer: (answers[q.id] || "").trim(), maps_to: q.maps_to || null }));
    onCreateWithAnswers(collected);
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(v) => {
        if (!v) onCancel();
      }}
    >
      <DialogContent className="max-w-xl gap-0 p-0 overflow-hidden" data-testid="preanalyze-dialog">
        <DialogHeader className="px-6 pt-6 pb-4 border-b border-slate-100">
          <DialogTitle
            className="flex items-center gap-2.5 text-lg font-semibold text-slate-900"
            data-testid="preanalyze-dialog-title-normal"
          >
            <span className="flex items-center justify-center w-8 h-8 rounded-full shrink-0 bg-slate-900 text-white">
              <Sparkles className="w-4 h-4" />
            </span>
            Ein paar optionale Rückfragen
          </DialogTitle>
          {data?.reason && (
            <DialogDescription
              data-testid="preanalyze-reason"
              className="text-sm text-slate-600 leading-relaxed pt-1"
            >
              {data.reason}
            </DialogDescription>
          )}
        </DialogHeader>

        <div className="px-6 py-4 max-h-[65vh] overflow-y-auto">
          <p className="text-xs text-slate-500 mb-4">
            Alle Fragen sind freiwillig – Sie können einzelne überspringen oder direkt erstellen.
          </p>

          <div className="space-y-5">
            {questions.map((q, idx) => (
              <div
                key={q.id || idx}
                data-testid={`preanalyze-question-${idx}`}
                className="rounded-lg border border-slate-200 bg-slate-50/50 p-4"
              >
                <label className="block text-[15px] font-semibold text-slate-900 leading-snug">
                  {q.question}
                </label>
                {q.why && (
                  <p className="text-xs text-slate-500 mt-1 leading-relaxed">{q.why}</p>
                )}
                <Textarea
                  value={answers[q.id] || ""}
                  onChange={(e) => setAnswer(q.id, e.target.value)}
                  data-testid={`preanalyze-answer-${idx}`}
                  rows={3}
                  placeholder="Optional – hier antworten"
                  className="mt-3 min-h-[76px] bg-white border-slate-300 px-3 py-2 text-sm leading-relaxed resize-none focus-visible:ring-slate-900"
                />
                {provenance[q.id] && (
                  <p
                    data-testid={`preanalyze-provenance-${idx}`}
                    className="mt-2 inline-flex items-center gap-1 text-xs text-emerald-600"
                  >
                    <Check className="w-3.5 h-3.5 shrink-0" aria-hidden="true" />
                    Aus {provenance[q.id]} übernommen
                  </p>
                )}
              </div>
            ))}
          </div>

          {hasDeviceQuestions && onUploadNameplate && (
            <div className="mt-5">
              <Button
                type="button"
                variant="outline"
                onClick={() => onUploadNameplate()}
                disabled={submitting}
                data-testid="preanalyze-nameplate-upload"
                className="w-full border-dashed border-slate-300 text-slate-600 hover:text-slate-900 hover:bg-slate-50"
              >
                <Camera className="w-4 h-4 mr-2" aria-hidden="true" />
                Typenschild fotografieren oder hochladen
              </Button>
              <p className="mt-1.5 text-center text-[11px] text-slate-400">
                Optional – füllt Hersteller, Modell und Artikelnummer automatisch aus.
              </p>
            </div>
          )}
        </div>

        <DialogFooter className="px-6 py-4 border-t border-slate-100 flex-col-reverse gap-2 sm:flex-row sm:items-center sm:justify-between sm:gap-3">
          <Button
            variant="ghost"
            onClick={onCancel}
            disabled={submitting}
            data-testid="preanalyze-cancel"
            className="text-slate-500 hover:text-slate-800"
          >
            <X className="w-4 h-4 mr-1.5" />
            Abbrechen
          </Button>
          <div className="flex flex-col-reverse gap-2 sm:flex-row sm:items-center sm:gap-2">
            <Button
              variant="outline"
              onClick={() => onDirectCreate()}
              disabled={submitting}
              data-testid="preanalyze-direct-create"
              className="border-slate-300"
            >
              Direkt erstellen
            </Button>
            <Button
              onClick={handleWithAnswers}
              disabled={submitting}
              data-testid="preanalyze-create-with-answers"
              className="bg-slate-900 hover:bg-slate-800 text-white"
            >
              Mit Antworten erstellen
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
